import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Phone, Pencil, Plus, ShieldAlert, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = {
  name: "",
  blood_type: "",
  date_of_birth: "",
  allergies: "",
  medications: "",
  conditions: "",
  notes: "",
  contacts: [],
};

const Field = ({ label, value, big }) => (
  <div className="border border-border bg-[#141414] p-6">
    <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">{label}</p>
    <p
      className={
        big
          ? "font-display font-black tracking-tighter text-3xl sm:text-4xl mt-3 leading-none"
          : "text-base mt-3 leading-relaxed whitespace-pre-wrap"
      }
    >
      {value || "—"}
    </p>
  </div>
);

export default function TagProfilePage() {
  const { tagId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tag, setTag] = useState(null);
  const [notFoundError, setNotFoundError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/tags/${tagId}`);
      setTag(data);
      setForm({ ...EMPTY, ...(data.profile || {}) });
    } catch (e) {
      setNotFoundError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    setTag(null);
    setEditing(false);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagId, user]);

  const claim = async () => {
    if (!user) {
      navigate(`/register?claim=${tagId.toUpperCase()}`);
      return;
    }
    try {
      const { data } = await api.post(`/tags/${tagId}/claim`);
      setTag(data);
      setForm({ ...EMPTY, ...(data.profile || {}) });
      setEditing(true);
      toast.success("Profile claimed. Add your medical details.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/tags/${tagId}`, form);
      setTag(data);
      setEditing(false);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (notFoundError)
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="tag-error">
        <p className="text-primary font-semibold">{notFoundError}</p>
      </div>
    );

  if (!tag)
    return (
      <div className="min-h-[50vh] grid place-items-center text-xs tracking-[0.2em] uppercase text-muted-foreground">
        Loading profile
      </div>
    );

  const inputCls =
    "mt-2 rounded-none bg-[#141414] border-border focus:ring-2 focus:ring-primary text-white";
  const contacts = tag.profile?.contacts || [];

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 pb-32" data-testid="tag-profile-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-primary">
            Tag {tag.tag_id}
          </p>
          <h1
            data-testid="profile-name"
            className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-3"
          >
            {tag.claimed ? tag.profile?.name || "Unnamed" : "Unclaimed tag"}
          </h1>
        </div>
        {tag.is_owner && !editing && (
          <Button
            data-testid="edit-profile-button"
            onClick={() => setEditing(true)}
            variant="outline"
            className="rounded-none border-border gap-2 text-xs tracking-[0.15em] uppercase font-bold h-11"
          >
            <Pencil className="w-4 h-4" /> Edit
          </Button>
        )}
      </div>

      {!tag.claimed && (
        <div
          data-testid="unclaimed-panel"
          className="mt-10 border border-primary/50 bg-primary/10 p-8"
        >
          <ShieldAlert className="w-7 h-7 text-primary" />
          <h2 className="font-display text-2xl font-black tracking-tight mt-5">
            This tag has no owner yet
          </h2>
          <p className="mt-3 text-sm text-muted-foreground max-w-md leading-relaxed">
            Claim it to lock this emergency profile to your account. Only you will be able to
            edit it afterwards.
          </p>
          <Button
            data-testid="claim-profile-button"
            onClick={claim}
            className="mt-7 h-12 rounded-none bg-primary hover:bg-[#FF5C53] tracking-[0.15em] uppercase text-xs font-bold px-8"
          >
            Claim this profile
          </Button>
        </div>
      )}

      {tag.claimed && !editing && (
        <>
          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            <Field label="Blood Type" value={tag.profile?.blood_type} big />
            <Field label="Date of birth" value={tag.profile?.date_of_birth} big />
            <div className="sm:col-span-2">
              <Field label="Allergies" value={tag.profile?.allergies} big />
            </div>
            <Field label="Medications" value={tag.profile?.medications} />
            <Field label="Conditions" value={tag.profile?.conditions} />
          </div>

          <div className="mt-5 border border-border bg-[#141414] p-6 sm:p-8">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">
              Additional notes
            </p>
            <p
              data-testid="profile-notes"
              className="text-base sm:text-lg mt-4 leading-relaxed whitespace-pre-wrap min-h-[6rem]"
            >
              {tag.profile?.notes || "No additional notes provided."}
            </p>
          </div>

          <div className="mt-5">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-4">
              Emergency contacts
            </p>
            {contacts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No contacts listed.</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {contacts.map((c, i) => (
                  <a
                    key={i}
                    href={`tel:${c.phone}`}
                    data-testid={`call-contact-${i}`}
                    className="border border-border bg-[#141414] p-5 flex items-center justify-between gap-4 hover:border-primary"
                    style={{ transition: "border-color .2s ease" }}
                  >
                    <div>
                      <p className="font-display font-black tracking-tight text-lg">{c.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {c.relationship} · {c.phone}
                      </p>
                    </div>
                    <span className="w-11 h-11 grid place-items-center bg-primary shrink-0">
                      <Phone className="w-5 h-5 text-white" />
                    </span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {contacts[0]?.phone && (
            <a
              href={`tel:${contacts[0].phone}`}
              data-testid="sticky-call-button"
              className="fixed bottom-0 left-0 right-0 z-20 bg-primary text-white h-16 flex items-center justify-center gap-3 font-display font-black tracking-tight text-lg"
            >
              <Phone className="w-5 h-5" /> Call {contacts[0].name || "contact"}
            </a>
          )}
        </>
      )}

      {tag.is_owner && editing && (
        <div className="mt-10 space-y-5" data-testid="edit-form">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
                Full name
              </Label>
              <Input
                data-testid="edit-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={`${inputCls} h-12`}
              />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
                Blood type
              </Label>
              <Input
                data-testid="edit-blood-type-input"
                value={form.blood_type}
                onChange={(e) => setForm({ ...form, blood_type: e.target.value })}
                placeholder="O+"
                className={`${inputCls} h-12`}
              />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
                Date of birth
              </Label>
              <Input
                data-testid="edit-dob-input"
                value={form.date_of_birth}
                onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                placeholder="1990-04-12"
                className={`${inputCls} h-12`}
              />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
                Conditions
              </Label>
              <Input
                data-testid="edit-conditions-input"
                value={form.conditions}
                onChange={(e) => setForm({ ...form, conditions: e.target.value })}
                className={`${inputCls} h-12`}
              />
            </div>
          </div>

          <div>
            <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
              Allergies
            </Label>
            <Textarea
              data-testid="edit-allergies-input"
              value={form.allergies}
              onChange={(e) => setForm({ ...form, allergies: e.target.value })}
              className={`${inputCls} min-h-[80px]`}
            />
          </div>
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
              Medications
            </Label>
            <Textarea
              data-testid="edit-medications-input"
              value={form.medications}
              onChange={(e) => setForm({ ...form, medications: e.target.value })}
              className={`${inputCls} min-h-[80px]`}
            />
          </div>
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
              Additional notes
            </Label>
            <Textarea
              data-testid="edit-notes-input"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className={`${inputCls} min-h-[180px]`}
            />
          </div>

          <div className="border border-border bg-[#141414] p-6">
            <div className="flex items-center justify-between">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">
                Emergency contacts
              </p>
              <Button
                type="button"
                data-testid="add-contact-button"
                onClick={() =>
                  setForm({
                    ...form,
                    contacts: [...form.contacts, { name: "", relationship: "", phone: "" }],
                  })
                }
                variant="outline"
                className="rounded-none border-border gap-2 text-xs uppercase font-bold h-9"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </Button>
            </div>
            <div className="mt-5 space-y-4">
              {form.contacts.map((c, i) => (
                <div key={i} className="grid gap-3 sm:grid-cols-[1fr_1fr_1fr_auto] items-center">
                  <Input
                    data-testid={`edit-contact-name-${i}`}
                    placeholder="Name"
                    value={c.name}
                    onChange={(e) => {
                      const next = [...form.contacts];
                      next[i] = { ...c, name: e.target.value };
                      setForm({ ...form, contacts: next });
                    }}
                    className="rounded-none bg-[#0A0A0A] border-border h-11 text-white"
                  />
                  <Input
                    data-testid={`edit-contact-relationship-${i}`}
                    placeholder="Relationship"
                    value={c.relationship}
                    onChange={(e) => {
                      const next = [...form.contacts];
                      next[i] = { ...c, relationship: e.target.value };
                      setForm({ ...form, contacts: next });
                    }}
                    className="rounded-none bg-[#0A0A0A] border-border h-11 text-white"
                  />
                  <Input
                    data-testid={`edit-contact-phone-${i}`}
                    placeholder="+1 555 0100"
                    value={c.phone}
                    onChange={(e) => {
                      const next = [...form.contacts];
                      next[i] = { ...c, phone: e.target.value };
                      setForm({ ...form, contacts: next });
                    }}
                    className="rounded-none bg-[#0A0A0A] border-border h-11 text-white"
                  />
                  <Button
                    type="button"
                    data-testid={`remove-contact-${i}`}
                    onClick={() =>
                      setForm({ ...form, contacts: form.contacts.filter((_, j) => j !== i) })
                    }
                    variant="ghost"
                    className="h-11 w-11 p-0 rounded-none hover:text-primary"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              onClick={save}
              disabled={saving}
              data-testid="save-profile-button"
              className="h-12 rounded-none bg-primary hover:bg-[#FF5C53] tracking-[0.15em] uppercase text-xs font-bold px-8"
            >
              {saving ? "Saving" : "Save profile"}
            </Button>
            <Button
              onClick={() => {
                setForm({ ...EMPTY, ...(tag.profile || {}) });
                setEditing(false);
              }}
              data-testid="cancel-edit-button"
              variant="outline"
              className="h-12 rounded-none border-border gap-2 text-xs tracking-[0.15em] uppercase font-bold"
            >
              <X className="w-4 h-4" /> Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
